import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { ArrowLeft, CheckCircle2, Loader2, Download, ExternalLink, AlertTriangle, Boxes, Hammer, Network } from "lucide-react";
import { toast } from "sonner";

const STATUS_MAP = {
  planning_architecture: { label: "Pianificazione architettura...", color: "#0F3D6B", spin: true },
  awaiting_plan_confirmation: { label: "Architettura proposta — approva", color: "#a16207" },
  generating_spec: { label: "Generazione Documento di Specifica...", color: "#0F3D6B", spin: true },
  awaiting_spec_confirmation: { label: "Specifica pronta — conferma", color: "#a16207" },
  building: { label: "Costruzione composable...", color: "#0F3D6B", spin: true },
  preview_ready: { label: "Anteprima pronta", color: "#15803d" },
  failed: { label: "Errore", color: "#C8102E" },
};

export default function ComponiApp() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [modifyText, setModifyText] = useState("");
  const [approving, setApproving] = useState(false);
  const [checks, setChecks] = useState({ limiti: false, revisione: false });
  const pollRef = useRef(null);

  const load = () => api.get(`/composable-apps/${id}`).then((r) => setData(r.data));

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 4000);
    return () => clearInterval(pollRef.current);
  }, [id]);

  if (!data) return <div className="adr-skeleton h-96" />;

  const status = data.status;
  const meta = STATUS_MAP[status] || STATUS_MAP.planning_architecture;
  const plan = data.architecture_plan;

  const approveArch = async () => {
    setApproving(true);
    try {
      await api.post(`/composable-apps/${id}/approve-architecture`);
      toast.success("Architettura approvata");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setApproving(false); }
  };

  const modifyArch = async () => {
    if (!modifyText.trim()) return;
    setApproving(true);
    try {
      await api.post(`/composable-apps/${id}/modify-architecture`, { content: modifyText });
      setModifyText("");
      toast.success("Modifica architettura in corso");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setApproving(false); }
  };

  const approveSpec = async () => {
    if (!checks.limiti || !checks.revisione) return;
    setApproving(true);
    try {
      await api.post(`/composable-apps/${id}/approve-specification`);
      toast.success("Specifica approvata. Avvio costruzione.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setApproving(false); }
  };

  const openPreview = () => {
    const token = localStorage.getItem("adriai_token");
    fetch(`${API}/composable-apps/${id}/preview`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.text()).then((html) => {
        const w = window.open(); w.document.open(); w.document.write(html); w.document.close();
      });
  };

  const download = async () => {
    const token = localStorage.getItem("adriai_token");
    const r = await fetch(`${API}/composable-apps/${id}/download`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) { toast.error("Pacchetto non pronto"); return; }
    const b = await r.blob(); const u = URL.createObjectURL(b);
    const a = document.createElement("a"); a.href = u; a.download = data.package_file; a.click();
    URL.revokeObjectURL(u);
  };

  return (
    <div className="max-w-5xl">
      <Link to="/componi" className="inline-flex items-center gap-1.5 text-sm text-[#555555] hover:text-[#0F3D6B] mb-6">
        <ArrowLeft className="w-4 h-4" /> Le mie app composable
      </Link>

      <h1 className="serif text-2xl font-semibold tracking-tight mb-1" data-testid="comp-title">{data.title}</h1>
      <div className="flex items-center gap-2 mb-6 text-xs text-[#555555]">
        {meta.spin ? <Loader2 className="w-4 h-4 animate-spin" style={{ color: meta.color }} /> : null}
        <span className="font-semibold capitalize" style={{ color: meta.color }} data-testid="comp-status">{meta.label}</span>
        <span>· v{data.current_version || 0}</span>
      </div>

      {status === "planning_architecture" && (
        <div className="adr-card p-8 text-center">
          <Loader2 className="w-10 h-10 mx-auto mb-3 animate-spin text-[#0F3D6B]" />
          <h3 className="serif font-semibold mb-1">Architecture Planning Agent al lavoro</h3>
          <p className="text-sm text-[#555555]">Sto analizzando il catalogo OSS e proponendo la combinazione migliore di componenti.</p>
        </div>
      )}

      {plan && status === "awaiting_plan_confirmation" && (
        <div className="space-y-4">
          <div className="adr-card p-6">
            <div className="adr-overline mb-3">Architecture Plan proposto</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5 text-sm">
              <Stat icon={Boxes} label="Componenti riusati" value={(plan.reused_components || []).length} />
              <Stat icon={Hammer} label="Da costruire" value={(plan.custom_components || []).length} />
              <Stat icon={Network} label="Fattibilità" value={`${plan.feasibility_score || "—"}/100`} />
            </div>

            <div className="mb-5">
              <div className="text-xs font-semibold text-[#0F3D6B] mb-2">📦 Componenti riusati dal catalogo</div>
              <div className="space-y-2">
                {(plan.reused_components || []).map((c, i) => (
                  <div key={i} className="p-3 rounded-md border border-[#E2E8F0] bg-[#F2F5F8]/40" data-testid={`reused-${i}`}>
                    <div className="font-semibold text-[#1A1A1A] text-sm">{c.project_slug}</div>
                    <div className="text-xs text-[#555555] mt-1"><strong>Ruolo:</strong> {c.role_in_app_it}</div>
                    {c.license_note_it && <div className="text-xs text-amber-700 mt-1">⚠ {c.license_note_it}</div>}
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-5">
              <div className="text-xs font-semibold text-[#C8102E] mb-2">🔨 Componenti da costruire da zero</div>
              <div className="space-y-2">
                {(plan.custom_components || []).map((c, i) => (
                  <div key={i} className="p-3 rounded-md border border-[#E2E8F0]" data-testid={`custom-${i}`}>
                    <div className="font-semibold text-[#1A1A1A] text-sm">{c.name}</div>
                    <div className="text-xs text-[#555555] mt-1">{c.purpose_it}</div>
                    <div className="text-xs italic text-[#555555] mt-1"><strong>Perché non riuso:</strong> {c.why_not_reuse_it}</div>
                  </div>
                ))}
              </div>
            </div>

            {plan.integrations?.length > 0 && (
              <div className="mb-5">
                <div className="text-xs font-semibold text-[#0F3D6B] mb-2">🔗 Integrazioni</div>
                <ul className="space-y-1.5 text-sm">
                  {plan.integrations.map((i, idx) => (
                    <li key={idx} className="flex gap-2"><span className="adr-badge text-xs" style={{ background: "rgba(15,61,107,0.08)", color: "#0F3D6B" }}>{i.pattern}</span><span>{i.description_it}</span></li>
                  ))}
                </ul>
              </div>
            )}

            {plan.estimated_resources && (
              <div className="text-xs text-[#555555] mb-4">
                Risorse stimate: {plan.estimated_resources.cpu} vCPU · {plan.estimated_resources.ram_gb} GB RAM · {plan.estimated_resources.disk_gb} GB · max {plan.estimated_resources.max_users} utenti
              </div>
            )}

            {plan.warnings_it?.length > 0 && (
              <div className="p-3 rounded-md bg-amber-50 border border-amber-200 mb-4">
                <div className="flex items-center gap-2 text-xs font-semibold text-amber-900 mb-1"><AlertTriangle className="w-4 h-4" /> Avvertimenti</div>
                <ul className="text-xs text-amber-900 list-disc ml-5 space-y-0.5">
                  {plan.warnings_it.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}

            <div className="flex gap-2 flex-wrap">
              <button onClick={approveArch} disabled={approving} className="adr-btn-primary text-sm" data-testid="approve-architecture">
                <CheckCircle2 className="w-4 h-4 inline mr-1" /> {approving ? "..." : "Approva architettura"}
              </button>
            </div>
          </div>

          <div className="adr-card p-5">
            <div className="adr-overline mb-2">Chiedi una modifica all'architettura</div>
            <textarea value={modifyText} onChange={(e) => setModifyText(e.target.value)} placeholder='Es. "Vorrei usare Mattermost invece di Rocket.Chat" oppure "Semplifica, togli Keycloak"' className="adr-input min-h-[70px]" data-testid="modify-arch-input" />
            <button onClick={modifyArch} disabled={approving || !modifyText.trim()} className="adr-btn-outline text-sm mt-2" data-testid="modify-arch-btn">
              Rigenera con questa modifica
            </button>
          </div>
        </div>
      )}

      {(status === "generating_spec" || status === "building") && (
        <div className="adr-card p-8 text-center">
          <Loader2 className="w-10 h-10 mx-auto mb-3 animate-spin text-[#0F3D6B]" />
          <h3 className="serif font-semibold mb-1">
            {status === "generating_spec" ? "Generazione Documento di Specifica..." : "Composable Builder al lavoro..."}
          </h3>
          <p className="text-sm text-[#555555]">Pochi minuti.</p>
        </div>
      )}

      {data.specification_document && status === "awaiting_spec_confirmation" && (
        <div className="adr-card p-6">
          <div className="adr-overline mb-3">Documento di Specifica</div>
          {data.key_warnings?.length > 0 && (
            <div className="p-3 rounded-md bg-amber-50 border border-amber-200 mb-4">
              <div className="flex items-center gap-2 text-xs font-semibold text-amber-900 mb-1"><AlertTriangle className="w-4 h-4" /> Punti chiave da leggere</div>
              <ul className="text-xs text-amber-900 list-disc ml-5 space-y-0.5">
                {data.key_warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}
          <div className="prose prose-sm max-w-none text-[#1A1A1A] whitespace-pre-wrap text-sm leading-relaxed border-l-2 border-[#0F3D6B] pl-4 max-h-[500px] overflow-y-auto" data-testid="spec-doc">
            {data.specification_document}
          </div>
          <div className="border-t border-[#E2E8F0] mt-5 pt-5 space-y-3">
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={checks.limiti} onChange={(e) => setChecks({ ...checks, limiti: e.target.checked })} className="mt-0.5" data-testid="check-limiti" />
              <span>Ho letto i <strong>Limiti dichiarati</strong> dell'applicazione</span>
            </label>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={checks.revisione} onChange={(e) => setChecks({ ...checks, revisione: e.target.checked })} className="mt-0.5" data-testid="check-revisione" />
              <span>Comprendo che l'applicazione richiederà una <strong>revisione tecnica prima del go-live</strong></span>
            </label>
            <button onClick={approveSpec} disabled={!checks.limiti || !checks.revisione || approving} className="adr-btn-primary text-sm mt-2" data-testid="approve-specification">
              {approving ? "Avvio..." : "Approva specifica e costruisci"}
            </button>
          </div>
        </div>
      )}

      {status === "preview_ready" && (
        <div className="space-y-4">
          <div className="adr-card p-6 text-center">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-[#15803d]" />
            <h3 className="serif text-xl font-semibold mb-2">Applicazione composable pronta</h3>
            <p className="text-sm text-[#555555] mb-5">Versione {data.current_version}. Apri l'anteprima per esplorarla, poi scarica il pacchetto.</p>
            <div className="flex gap-2 justify-center flex-wrap">
              <button onClick={openPreview} className="adr-btn-secondary text-sm" data-testid="comp-preview-btn">
                <ExternalLink className="w-4 h-4 inline mr-1" /> Apri anteprima
              </button>
              <button onClick={download} className="adr-btn-primary text-sm" data-testid="comp-download-btn">
                <Download className="w-4 h-4 inline mr-1" /> Scarica pacchetto composable
              </button>
            </div>
          </div>
        </div>
      )}

      {status === "failed" && (
        <div className="adr-card p-5 border-l-4" style={{ borderLeftColor: "#C8102E", background: "rgba(200,16,46,0.04)" }}>
          <div className="text-sm font-semibold text-[#C8102E] mb-1">Errore</div>
          <div className="text-sm">{data.error_message}</div>
        </div>
      )}
    </div>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className="p-3 rounded-md border border-[#E2E8F0] bg-white">
    <div className="flex items-center gap-2 mb-1">
      <Icon className="w-4 h-4 text-[#0F3D6B]" />
      <div className="text-xs text-[#555555]">{label}</div>
    </div>
    <div className="serif text-xl font-semibold text-[#1A1A1A]">{value}</div>
  </div>
);
